import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'

import { useApiClient } from 'clients/yogi-api'
import type { FilterCriterion } from 'features/trend-detection/types'
import { buildBenchmarkingFiltersV2 } from 'features/trend-detection/utils/agent-filters-v2'

export interface BenchmarkingGroupStats {
  record_count: number
  sentence_count: number
  product_count: number
  brand_count: number
  avg_rating: number | null
  avg_sentiment: number | null
}

export interface BenchmarkingCompareStatsResponse {
  // `null` when `focus_group_filters` is empty — the backend skips computing
  // a redundant, misleading "focus" block that would otherwise equal
  // `comparison` (focus = cohort + [] === cohort). See ComputeCompareStatsUseCase.
  focus: BenchmarkingGroupStats | null
  comparison: BenchmarkingGroupStats
  // Human-readable label for the trailing analysis window the backend
  // applied (e.g. "last 90 days") — server-derived so it can't silently
  // diverge from the actual default if that ever changes.
  date_window_label: string
}

export interface UseBenchmarkCompareStatsParams {
  projectId: string | null
  cohortFilters: FilterCriterion[]
  focusGroupFilters: FilterCriterion[]
}

export interface UseBenchmarkCompareStatsResult {
  stats: BenchmarkingCompareStatsResponse | null
  isLoading: boolean
  /** True when the request failed. The widget degrades to an "unavailable"
   * message rather than blocking the form — mirrors how `validateFilters`
   * (use-benchmarking-setup.ts) treats a failed round-trip as non-fatal. */
  isUnavailable: boolean
}

const DEBOUNCE_MS = 400

/**
 * Debounced `POST /benchmarking/agents/compare-stats` — the live Universe/
 * Focus stats shown on `BenchmarkUniversePanel`'s two summary rows. Both
 * filter arrays are debounced together so a chiclet add/remove on either
 * side doesn't fire a request per edit.
 *
 * Unlike the pre-rework version, the query is NOT gated on
 * `focusGroupFilters.length > 0` — the Universe row is meant to show a live
 * count before any focus is picked, which is the whole point of nesting
 * Focus inside Universe. The two debounced states are seeded from the
 * initial props (not started empty) so the very first render already
 * reflects the current cohort — an unseeded empty start would fire one
 * request at t=0 with `cohort_filters: []` (a whole-hub count) that flashes
 * in the Universe header before the real cohort lands 400ms later.
 */
export function useBenchmarkCompareStats({
  projectId,
  cohortFilters,
  focusGroupFilters,
}: UseBenchmarkCompareStatsParams): UseBenchmarkCompareStatsResult {
  const apiClient = useApiClient()

  const [debouncedCohortFilters, setDebouncedCohortFilters] = useState<
    FilterCriterion[]
  >(() => cohortFilters)
  const [debouncedFocusGroupFilters, setDebouncedFocusGroupFilters] = useState<
    FilterCriterion[]
  >(() => focusGroupFilters)

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedCohortFilters(cohortFilters)
      setDebouncedFocusGroupFilters(focusGroupFilters)
    }, DEBOUNCE_MS)
    return () => clearTimeout(id)
  }, [cohortFilters, focusGroupFilters])

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      'benchmarking-compare-stats',
      projectId,
      JSON.stringify(debouncedCohortFilters),
      JSON.stringify(debouncedFocusGroupFilters),
    ],
    queryFn: async () => {
      const { data } = await apiClient.post(
        `/benchmarking/agents/compare-stats?proj_uuid=${projectId}`,
        buildBenchmarkingFiltersV2(
          debouncedCohortFilters,
          debouncedFocusGroupFilters,
        ),
      )
      return data as BenchmarkingCompareStatsResponse
    },
    enabled: !!projectId,
    refetchOnWindowFocus: false,
    retry: false,
  })

  return {
    stats: data ?? null,
    isLoading,
    isUnavailable: isError,
  }
}
